
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import WLSTableFilterable from '@/components/reports-v3/components/filterables/Customize/WLSTableFilterable.vue';
import FilterWidget from '@/components/reports-v3/components/codedWidgets/shared/FilterWidget.vue';
import {
	PermissionsGroup,
	ValueText,
} from '@/store/models.def';
import moment from 'moment';
import gdbx from '@/store/modules/gdbx';
import { roundToFixed } from '@/util/number';
import Checkbox from 'primevue/checkbox';
import InputNumber from 'primevue/inputnumber';
import { getTableName } from '@/worker/db/sqlHelpers';

@Component({
	components: { WLSTableFilterable, Checkbox, InputNumber },
})
export default class RecommendationManualReorderTable extends FilterWidget {
	public get permissionIds(): PermissionsGroup[] {
		return ['stocks'];
	}

	public selectedLeadTime: number | null = null;
	public isIncludeInactive: boolean = false;
	public isOnlyFilledNewReorder: boolean = false;
	public isShowDetails: boolean = false;
	public selectedStatus: string = '';

	public filterIds: Array<
		| 'date'
		| 'dateAsOf'
		| 'dateRange'
		| 'stockItems'
		| 'agents'
		| 'customers'
		| 'suppliers'
	> = [];

	public tableItems: any[] = [];
	public tableFields: any[] = [];

	public get statusTypeList() {
		let list: Array<ValueText<string>> = [];

		list = [
			{ value: 'T', text: 'Active' },
			{ value: 'F', text: 'Inactive' },
		];

		return list;
	}

	public get exportFileName() {
		const formattedDate = moment(this.selectedAsOfDate).format('DD MMM YY');

		return 'Reorder Analysis(Manual)' + '_' + 'As Of ' + formattedDate;
	}

	public get dateFormatted() {
		return this.selectedDateRange;
	}

	public get numDecimal() {
		return gdbx.numDecimal;
	}

	public get dayDiff() {
		return (
			moment(this.selectedDateRange[1]).diff(
				this.selectedDateRange[0],
				'days',
			) + 1
		);
	}

	public get updatedTableFields() {
		const tableFieldClone = [...this.tableFields];

		const warehousePosition = tableFieldClone.findIndex(
			(field) => field.key === 'warehouse_qty',
		);

		if (!this.isShowDetails) {
			tableFieldClone.splice(warehousePosition, 4);
		}

		return tableFieldClone;
	}

	public get updatedTableItems() {
		const newItems: any[] = [];

		if (this.isIncludeInactive && this.isOnlyFilledNewReorder) {
			this.tableItems.filter((item) => {
				if (
					item.hasOwnProperty('order_decision') &&
					(item.order_decision || item.order_decision === 0)
				) {
					newItems.push(item);
				}
			});
			return newItems.length > 0 ? newItems : this.tableItems;
		} else if (this.isIncludeInactive && !this.isOnlyFilledNewReorder) {
			return this.tableItems;
		} else if (!this.isIncludeInactive && this.isOnlyFilledNewReorder) {
			this.tableItems
				.filter((item) => item.isActive === 'True')
				.filter(
					(item) => item.isDiscontinued !== 'True' || item.isDiscontinued === null,
				)
				.filter((item) => {
					if (
						item.hasOwnProperty('order_decision') &&
						(item.order_decision || item.order_decision === 0)
					) {
						newItems.push(item);
					}
				});

			return newItems.length > 0
				? newItems
				: this.tableItems
						.filter((item) => item.isActive === 'True')
						.filter(
							(item) =>
								item.isDiscontinued !== 'True' || item.isDiscontinued === null,
						);
		} else if (!this.isIncludeInactive && !this.isOnlyFilledNewReorder) {
			return this.tableItems
				.filter((item) => item.isActive === 'True')
				.filter(
					(item) => item.isDiscontinued !== 'True' || item.isDiscontinued === null,
				);
		}
	}

	public get expensiveHook() {
		const {
			selectedAsOfDate,
			selectedDateRange,
			selectedLeadTime,
			dayDiff,
			selectedStockItems,
		} = this;
		return JSON.stringify([
			selectedAsOfDate,
			selectedDateRange,
			selectedLeadTime,
			dayDiff,
			selectedStockItems,
		]);
	}

	public async expensiveCalc() {
		const jobId = this.currentJobId;
		this.generateLoadingText(0.2, 0.3);

		// Build manual filter clauses
		const escapeStr = (s: string) => s.replace(/'/g, '\'\'');
		let stockItemFilter = '';
		let stockItemCodeFilter = '';
		if (this.selectedStockItems.length > 0) {
			const list = this.selectedStockItems.map((s) => `'${escapeStr(s)}'`).join(',');
			stockItemFilter = ` AND "ITEMCODE" IN (${list})`;
			stockItemCodeFilter = ` AND "CODE" IN (${list})`;
		}

		const asOfDate = this.selectedAsOfDate;
		const dateStart = this.selectedDateRange[0];
		const dateEnd = this.selectedDateRange[1];
		const salesOrderTable = getTableName('salesOrder');
		const purchaseOrderTable = getTableName('purchaseOrder');
		const purchaseRequestTable = getTableName('purchaseRequest');
		const stockTransTable = getTableName('diyStockTrans');
		const globalStockTable = getTableName('diyGlobalStocks');

		const queryHead = await this._ensureTablesLoaded([
			'salesOrder', 'purchaseOrder', 'purchaseRequest', 'diyStockTrans', 'diyGlobalStocks',
		] as any[]);
		this.checkInterrupted(jobId);
		this.generateLoadingText(0.3, 0.9);

		// Fetch aggregated data in parallel using rawQuery
		const [
			salesOrderData,
			purchaseOrderData,
			purchaseRequestData,
			asOfStockTransData,
			stockTransData,
			globalStockData,
		] = await Promise.all([
			// Sales Order: SUM outstanding quantity by item
			queryHead.rawQuery(`
				SELECT "ITEMCODE" as itemCode,
					SUM(TRY_CAST("OSQTY" AS DOUBLE)) as osQty
				FROM ${salesOrderTable}
				WHERE TRY_CAST("DOCDATE" AS BIGINT) <= ${asOfDate}
					${stockItemFilter}
				GROUP BY "ITEMCODE"
			`),
			// Purchase Order: SUM outstanding quantity by item
			queryHead.rawQuery(`
				SELECT "ITEMCODE" as itemCode,
					SUM(TRY_CAST("OSQTY" AS DOUBLE)) as osQty
				FROM ${purchaseOrderTable}
				WHERE TRY_CAST("DOCDATE" AS BIGINT) <= ${asOfDate}
					${stockItemFilter}
				GROUP BY "ITEMCODE"
			`),
			// Purchase Request: SUM outstanding quantity by item
			queryHead.rawQuery(`
				SELECT "ITEMCODE" as itemCode,
					SUM(TRY_CAST("OSQTY" AS DOUBLE)) as osQty
				FROM ${purchaseRequestTable}
				WHERE TRY_CAST("DOCDATE" AS BIGINT) <= ${asOfDate}
					${stockItemFilter}
				GROUP BY "ITEMCODE"
			`),
			// Stock Trans (asOf): SUM quantity by item
			queryHead.rawQuery(`
				SELECT "ITEMCODE" as itemCode,
					SUM(TRY_CAST("QTY" AS DOUBLE)) as qty
				FROM ${stockTransTable}
				WHERE TRY_CAST("POSTDATE" AS BIGINT) <= ${asOfDate}
					${stockItemFilter}
				GROUP BY "ITEMCODE"
			`),
			// Stock Trans (dateRange): SUM abs(quantityOut) by item
			queryHead.rawQuery(`
				SELECT "ITEMCODE" as itemCode,
					SUM(ABS(TRY_CAST("QTYOUT" AS DOUBLE))) as qtyOut
				FROM ${stockTransTable}
				WHERE TRY_CAST("POSTDATE" AS BIGINT) BETWEEN ${dateStart} AND ${dateEnd}
					${stockItemFilter}
				GROUP BY "ITEMCODE"
			`),
			// Global Stock: get lead time, reorder qty, isActive, isDiscontinued
			queryHead.rawQuery(`
				SELECT "CODE" as itemCode,
					TRY_CAST("LEADTIME" AS DOUBLE) as leadTime,
					TRY_CAST("REORDERQTY" AS DOUBLE) as reorderQty,
					"ISACTIVE" as isActive,
					"UDF_DISCON" as isDiscontinued
				FROM ${globalStockTable}
				WHERE 1=1${stockItemCodeFilter}
			`),
		]);
		this.checkInterrupted(jobId);

		// Build lookup maps
		const salesOrderMap: Map<string, number> = new Map();
		for (const row of salesOrderData) {
			salesOrderMap.set(row.itemCode, Number(row.osQty) || 0);
		}

		const purchaseOrderMap: Map<string, number> = new Map();
		for (const row of purchaseOrderData) {
			purchaseOrderMap.set(row.itemCode, Number(row.osQty) || 0);
		}

		const purchaseRequestMap: Map<string, number> = new Map();
		for (const row of purchaseRequestData) {
			purchaseRequestMap.set(row.itemCode, Number(row.osQty) || 0);
		}

		const asOfStockTransMap: Map<string, number> = new Map();
		for (const row of asOfStockTransData) {
			asOfStockTransMap.set(row.itemCode, Number(row.qty) || 0);
		}

		const stockTransMap: Map<string, number> = new Map();
		for (const row of stockTransData) {
			stockTransMap.set(row.itemCode, Number(row.qtyOut) || 0);
		}

		interface GlobalStockInfo {
			leadTime: number;
			reorderQty: number;
			isActive: string;
			isDiscontinued: string | null;
		}
		const globalStockMap: Map<string, GlobalStockInfo> = new Map();
		for (const row of globalStockData) {
			globalStockMap.set(row.itemCode, {
				leadTime: Number(row.leadTime) || 0,
				reorderQty: Number(row.reorderQty) || 0,
				isActive: row.isActive || '',
				isDiscontinued: row.isDiscontinued || null,
			});
		}

		this.generateLoadingText(0.95);

		// Build result - only include items that exist in stockTransMap (dateRange)
		// and have corresponding asOfStockTrans and globalStock data
		const result: Array<{
			item_desc: string;
			warehouse_qty: number;
			shipping_qty: number;
			so_qty: number;
			ordered_qty: number;
			actual_qty: number;
			period_sales: number;
			daily_demand: number;
			lead_time_system: number;
			lead_time_edit: number;
			reorder_level: number;
			recommend_reorder_qty: number;
			order_decision: number | null;
			moq_qty: number;
			isActive: string;
			isDiscontinued: string | null;
		}> = [];

		for (const [itemCode, periodSales] of stockTransMap) {
			// Must have asOf stock trans and global stock data
			if (!asOfStockTransMap.has(itemCode) || !globalStockMap.has(itemCode)) {
				continue;
			}

			const quantity = asOfStockTransMap.get(itemCode) || 0;
			const globalInfo = globalStockMap.get(itemCode)!;
			const shippingQuantity = purchaseOrderMap.get(itemCode) || 0;
			const soQuantity = salesOrderMap.get(itemCode) || 0;
			const orderedQuantity = purchaseRequestMap.get(itemCode) || 0;

			const dailyDemand = roundToFixed(
				periodSales / this.dayDiff,
				this.numDecimal,
			);

			const leadTime = this.selectedLeadTime
				? this.selectedLeadTime
				: globalInfo.leadTime;

			const reorderLevel = Math.round(leadTime * dailyDemand * 100) / 100;

			const actualQty = shippingQuantity + quantity + orderedQuantity - soQuantity;

			const recommendReorder = Math.round((reorderLevel - actualQty) * 100) / 100;

			result.push({
				item_desc: gdbx.stockItemNames[itemCode],
				warehouse_qty: quantity,
				shipping_qty: shippingQuantity,
				so_qty: soQuantity,
				ordered_qty: orderedQuantity,
				actual_qty: actualQty,
				period_sales: periodSales,
				daily_demand: dailyDemand,
				lead_time_system: globalInfo.leadTime,
				lead_time_edit: leadTime,
				reorder_level: reorderLevel,
				recommend_reorder_qty: recommendReorder,
				order_decision: null,
				moq_qty: globalInfo.reorderQty,
				isActive: globalInfo.isActive,
				isDiscontinued: globalInfo.isDiscontinued,
			});
		}

		this.tableItems = result;

		this.tableFields = [
			{
				key: 'item_desc',
				sortable: true,
				tdClass: (value) => {
					if (value) {
						return 'custom-style';
					}
				},
				stickyColumn: true,
			},
			{
				key: 'warehouse_qty',
				label: 'Warehouse Qty',
				sortable: true,
				variant: 'info',
				class: 'text-center',
			},
			{
				key: 'shipping_qty',
				label: 'Shipping',
				sortable: true,
				variant: 'info',
				class: 'text-center',
			},
			{
				key: 'so_qty',
				label: 'SO Qty',
				sortable: true,
				variant: 'info',
				class: 'text-center',
			},
			{
				key: 'ordered_qty',
				label: 'Ordered',
				sortable: true,
				variant: 'info',
				class: 'text-center',
			},
			{
				key: 'actual_qty',
				label: 'Actual Qty',
				sortable: true,
				class: 'text-center',
			},
			{
				key: 'period_sales',
				sortable: true,
				class: 'text-center',
			},
			{
				key: 'daily_demand',
				sortable: true,
				class: 'text-center',
			},
			{
				key: 'lead_time_system',
				label: 'Lead Time (System)',
				sortable: true,
				class: 'text-center',
			},
			{
				key: 'lead_time_edit',
				label: 'Lead Time',
				sortable: true,
				class: 'text-center',
			},
			{
				key: 'reorder_level',
				sortable: true,
				// formatter: (value, key, item) => {
				// 	return (
				// 		Math.round(item.lead_time_system * item.daily_demand * 100) / 100
				// 	);
				// },
				class: 'text-center',
			},
			{
				key: 'recommend_reorder_qty',
				label: 'Recommend Order Qty',
				sortable: true,
				// formatter: (value, key, item) => {
				// 	return (
				// 		Math.round(
				// 			(Math.round(item.lead_time_system * item.daily_demand * 100) /
				// 				100 -
				// 				item.warehouse_qty) *
				// 				100,
				// 		) / 100
				// 	);
				// },
				class: 'text-center',
			},
			{
				key: 'moq_qty',
				label: 'MOQ',
				sortable: true,
				class: 'text-center',
			},
			{
				key: 'order_decision',
				label: 'Order Decision',
				sortable: false,
				class: 'text-center',
			},
		];

		this.generateLoadingText(1);
		this.saveHistory('tableItems', 'tableFields');
	}

	public onHovering(value: boolean, filterId: string) {
		this.$emit('hovering', value, filterId);
	}
	public onEditing(value: boolean, filterId: string) {
		this.$emit('editing', value, filterId);
	}
}
